import type { ReactNode } from 'react';
import { useId, useState } from 'react';
import styled from 'styled-components';

import { Collapsable } from '../../cdl/Collapsable/Collapsable';
import { Text } from '../../cdl/Text/Text';
import { IconChevronDown } from '../../common/icons/cdl/ChevronDown';
import { IconChevronRight } from '../../common/icons/cdl/ChevronRight';
import { Box } from '../../common/ui/Box';
import { theme } from '../../common/ui/theme';
import type { DemandVolumeSummary } from '../types/DemandVolumeSummary';

import { DemandDelta } from './DemandDelta';
import { DemandLiftingCount } from './DemandLiftingCount';
import { DemandVolume } from './DemandVolume';
import { ResultingDemandRow } from './ResultingDemandRow';

const Header = styled(ResultingDemandRow)`
    font-feature-settings: inherit;
    width: 100%;
    border: none;
    border-radius: ${theme.radii[4]};
    background: transparent;
    color: inherit;
    cursor: pointer;

    &:hover {
        background: ${theme.colors.background.subtle};
    }
`;

const LeadingLine = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: ${theme.space[16]};
    width: 1px;
    background-color: ${theme.colors.border.muted};
    transform: translateX(-50%);
`;

interface ResultingDemandCollapsableProps {
    title: string;
    summary: DemandVolumeSummary;
    showChanges: boolean;
    liftingCount: number;
    muted?: boolean;
    children: ReactNode;
}

export const ResultingDemandCollapsable = ({
    title,
    summary,
    showChanges,
    liftingCount,
    muted,
    children,
}: ResultingDemandCollapsableProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const contentId = useId();
    const Icon = isOpen ? IconChevronDown : IconChevronRight;

    return (
        <Collapsable
            isOpen={isOpen}
            header={
                <Header
                    as="button"
                    $showChanges={showChanges}
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={contentId}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <Box display="flex" alignItems="flex-start" gap={8}>
                        <Box flexShrink={0} display="flex" alignItems="center" height={24}>
                            <Icon width={16} height={16} />
                        </Box>
                        <Box display="flex" flexDirection="column" minWidth={0}>
                            <Text
                                variant="small"
                                fontWeight={600}
                                color={muted ? 'foreground.muted' : 'foreground.default'}
                            >
                                {title}
                            </Text>
                            <DemandLiftingCount count={liftingCount} />
                        </Box>
                    </Box>
                    {showChanges ? (
                        <>
                            <Text variant="small" color="foreground.subtle">
                                <DemandVolume volume={summary.historicalVolume} />
                            </Text>
                            <DemandDelta deltaVolume={summary.deltaVolume} deltaPercent={summary.deltaPercent} />
                        </>
                    ) : null}
                    <Text
                        variant="small"
                        fontWeight={muted ? 400 : 600}
                        color={muted ? 'foreground.muted' : 'foreground.default'}
                    >
                        <DemandVolume volume={summary.adjustedVolume} />
                    </Text>
                </Header>
            }
        >
            <Box id={contentId} paddingLeft={24} position="relative">
                <LeadingLine aria-hidden="true" />
                {children}
            </Box>
        </Collapsable>
    );
};
